import StageStatus from '@/components/shared/stage-status'
import BackButton from '@/components/ui/BackButton'
import TabButton from '@/components/ui/TabButton'
import {
  MED_REQUEST_STATUS_LABELS,
  getIdVerificationResetBlocker,
  isPostApprovalStatusLabel,
  medRequestStatusLabelToValue,
  medRequestStatusValueToLabel
} from '@/constants/statuses'
import { canResetIdVerification, isReadOnly } from '@/lib/permissions'
import { formatDateTime, scrollToSection } from '@/lib/utils'
import { useMedReqStatusStages } from '@/hooks/useMedReqStatusStages'
import {
  isPatchsPharmacyFirstMedReq,
  useMedReqDetailQuery,
  useSendVideoConsultationSmsMutation,
  useUpdateMedReqDetailsMutation
} from '@/services/med-req-detail'
import { useLinkedPatientsQuery } from '@/services/linked-patients'
import { useUser } from '@clerk/clerk-react'
import { Box, Button, CircularProgress, Grid, Tooltip, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import LinkedPatientsSection from '../../shared/LinkedPatientsSection'
import NavMenu from '../../shared/NavMenu'
import PatientFilesSection from '../../shared/patient-files/PatientFilesSection'
import Consultation from './sections/consultation'
import MedRequestHeader from './sections/header'
import NotesSection from './sections/notes-section'
import OnHoldHistory from './sections/on-hold-history'
import PatientData from './sections/patient-data'
import RejectSection from './sections/reject-section'
import ResetIdVerificationDialog from './sections/reset-id-verification-dialog'
import MedRequestTimeline from './sections/med-request-timeline'
import SendFanEmailDialog from './sections/send-fan-email-dialog'

export default function MedRequestDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useUser()
  const readOnly = isReadOnly()
  const [showTimeline, setShowTimeline] = useState(false)
  const [showFollowUpEmailDialog, setShowFollowUpEmailDialog] = useState(false)
  const [showResetIdVerificationDialog, setShowResetIdVerificationDialog] = useState(false)
  const queryClient = useQueryClient()
  const { data: medRequest, isLoading, isError } = useMedReqDetailQuery(id)
  const { stages, isLoading: isStagesLoading } = useMedReqStatusStages(medRequest?.id ?? '', medRequest?.creationDate)
  const updateDetailsMutation = useUpdateMedReqDetailsMutation()
  const sendVideoConsultationSmsMutation = useSendVideoConsultationSmsMutation()

  const handleStatusChange = async (stage: { label: string }) => {
    if (isReadOnly()) return
    if (!id) return
    const backendValue = medRequestStatusLabelToValue(stage.label)
    if (!backendValue) return

    await updateDetailsMutation.mutateAsync({ orderItemHash: id, data: { stage: backendValue } })
    queryClient.invalidateQueries({ queryKey: ['med-request-detail', id] })
    queryClient.invalidateQueries({ queryKey: ['timeline', 'medication_request', medRequest?.id] })
  }

  const { data: linkedPatients } = useLinkedPatientsQuery(
    medRequest?.patient?.linkedPatientHash,
    medRequest?.patient?.id
  )
  const hasLinkedPatients = !!linkedPatients?.length

  const hasConsultation = !!medRequest?.consultationData?.length
  const canSendVideoConsultationSms =
    !readOnly &&
    !!medRequest &&
    (user?.publicMetadata?.roles?.some(role => ['CS_MANAGER', 'CS_PHARMACY_MANAGER', 'ADMIN'].includes(role)) ??
      false) &&
    isPatchsPharmacyFirstMedReq(medRequest)
  // Temporarily hidden while the ID verification reset flow is reviewed. Flip to true to show it again.
  const SHOW_RE_VERIFY_ID_BUTTON = false
  const canReVerifyId = SHOW_RE_VERIFY_ID_BUTTON && canResetIdVerification()
  const idVerificationResetBlocker = getIdVerificationResetBlocker(medRequest?.stage)
  /** Withhold the post-approval transitions while we cannot say who approved the item. */
  const blockedStatusOptions = useMemo(() => {
    if (!medRequest) return []

    const currentStatusLabel = stages.at(-1)?.label ?? medRequestStatusValueToLabel(medRequest.stage)
    const hasReachedApproved =
      stages.some(stage => stage.label === 'Approved') || isPostApprovalStatusLabel(currentStatusLabel)

    if (!hasReachedApproved) return []

    const prescriberFullName =
      medRequest.prescriber?.fullName?.trim() ||
      `${medRequest.prescriber?.firstName ?? ''} ${medRequest.prescriber?.lastName ?? ''}`.trim()

    if (prescriberFullName) return []

    return MED_REQUEST_STATUS_LABELS.filter(isPostApprovalStatusLabel).map(status => ({
      status,
      reason: 'Approver information is missing'
    }))
  }, [medRequest, stages])

  const navItems = useMemo(
    () =>
      [
        { id: 'patient-details', title: 'Patient details' },
        hasLinkedPatients ? { id: 'linked-patients', title: 'Linked patients' } : null,
        { id: 'on-hold', title: 'On Hold History' },
        hasConsultation ? { id: 'consultation', title: 'Consultation' } : null,
        { id: 'order-files', title: 'Order files' },
        { id: 'rejection', title: 'Rejection' },
        { id: 'notes', title: 'Notes' }
      ].filter(Boolean) as { id: string; title: string }[],
    [hasConsultation, hasLinkedPatients]
  )

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError || !medRequest) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Medication request not found</Typography>
      </Box>
    )
  }

  const handleSendVideoConsultationSms = async () => {
    if (isReadOnly()) return
    if (!medRequest.orderPhone) {
      enqueueSnackbar('Patient mobile number is missing', { variant: 'error' })
      return
    }

    try {
      await sendVideoConsultationSmsMutation.mutateAsync({
        to: medRequest.orderPhone,
        vendorHash: medRequest.epharmacyVendor,
        patientName: medRequest.patient.firstName
      })
      enqueueSnackbar('Video consultation SMS sent', { variant: 'success' })
      queryClient.invalidateQueries({ queryKey: ['timeline', 'medication_request', medRequest.id] })
    } catch {
      enqueueSnackbar('Failed to send video consultation SMS', { variant: 'error' })
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, pb: 4 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <BackButton fallbackPath='/med-requests' defaultLabel='Back to Medication Requests' />
          <Typography variant='pageTitle' sx={{ fontSize: '24px' }}>
            Medication request
          </Typography>
          <TabButton active={showTimeline} onClick={() => setShowTimeline(prev => !prev)}>
            Timeline
          </TabButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {canReVerifyId && (
            <Tooltip
              title={
                idVerificationResetBlocker
                  ? `This medication request ${idVerificationResetBlocker} and cannot be sent back for ID verification`
                  : ''
              }
            >
              <span>
                <Button
                  variant='secondary'
                  onClick={() => setShowResetIdVerificationDialog(true)}
                  disabled={!!idVerificationResetBlocker}
                >
                  Re-verify ID
                </Button>
              </span>
            </Tooltip>
          )}
          {canSendVideoConsultationSms && (
            <Button
              variant='primary'
              onClick={handleSendVideoConsultationSms}
              disabled={sendVideoConsultationSmsMutation.isPending}
            >
              {sendVideoConsultationSmsMutation.isPending ? 'Sending video SMS...' : 'Send video consultation SMS'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Header Banner */}
      <MedRequestHeader medRequest={medRequest} />

      {showTimeline ? (
        <MedRequestTimeline medRequestId={medRequest.id} medicationName={medRequest.orderedItem.name} />
      ) : (
        <>
          {/* Medication Status Section */}
          <StageStatus
            key={isStagesLoading ? 'loading' : 'timeline'}
            stages={stages}
            config={{
              title: 'Medication status',
              statusOptions: MED_REQUEST_STATUS_LABELS,
              blockedStatusOptions,
              fallbackStatus: medRequestStatusValueToLabel(medRequest.stage),
              changedBy: undefined,
              updatedAt: formatDateTime(new Date()),
              previousStatus: stages.length >= 2 ? stages[stages.length - 2]?.label : undefined
            }}
            onStageAdd={readOnly ? undefined : handleStatusChange}
            readOnly={readOnly}
          />

          {/* Main Content with Sidebar */}
          <Grid container spacing={4}>
            {/* Main Content Area */}
            <Grid
              size={9}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4
              }}
            >
              {/* Patient Details + Medication Request + Order + Shipping */}
              <PatientData medRequest={medRequest} />

              <LinkedPatientsSection
                patientHash={medRequest.patient.id}
                linkedPatientHash={medRequest.patient.linkedPatientHash}
              />

              {/* On Hold History */}
              <OnHoldHistory medRequest={medRequest} readOnly={readOnly} />

              {/* Consultation */}
              <Consultation medRequest={medRequest} />

              {/* Files attached to this order */}
              <PatientFilesSection
                patientHash={medRequest.patient.id}
                orderNumber={medRequest.orderNumber}
                orderHash={medRequest.orderHash}
              />

              {/* Rejection */}
              <RejectSection medRequest={medRequest} readOnly={readOnly} />

              {/* Notes Section - Combined Pharmacy and Customer Service */}
              <Box id='notes'>
                <NotesSection
                  title='Pharmacy notes'
                  notes={medRequest.additionalNotes.pharmacist}
                  readOnly={readOnly}
                  onSave={async notes => {
                    if (!id) return
                    await updateDetailsMutation.mutateAsync({ orderItemHash: id, data: { notes } })
                    queryClient.invalidateQueries({ queryKey: ['med-request-detail', id] })
                  }}
                />
                <Box sx={{ mt: 3 }}>
                  <NotesSection
                    title='Customer Service Notes'
                    notes={medRequest.additionalNotes.customerService}
                    readOnly={readOnly}
                    onSave={async notes => {
                      if (!id) return
                      await updateDetailsMutation.mutateAsync({ orderItemHash: id, data: { cs_notes: notes } })
                      queryClient.invalidateQueries({ queryKey: ['med-request-detail', id] })
                    }}
                    actionButton={{
                      label: 'Send follow-up email',
                      onClick: () => setShowFollowUpEmailDialog(true)
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Right Sidebar */}
            <Grid
              size={3}
              sx={{
                position: 'sticky',
                top: '88px',
                alignSelf: 'flex-start'
              }}
            >
              <NavMenu items={navItems} onNavigate={scrollToSection} />
            </Grid>
          </Grid>
        </>
      )}

      <SendFanEmailDialog
        open={showFollowUpEmailDialog}
        medRequest={medRequest}
        onClose={() => setShowFollowUpEmailDialog(false)}
        categoryFilter='follow-up'
      />

      <ResetIdVerificationDialog
        open={showResetIdVerificationDialog}
        medRequest={medRequest}
        onClose={() => setShowResetIdVerificationDialog(false)}
      />
    </Box>
  )
}
